const config = JSON.parse(document.getElementById('cc-live-search-config').textContent);
const words = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
const compact = (value) => words(value).replace(/\s/g, '');
let catalogPromise;
const cacheKey = `cc-search-v1:${config.theme}:${config.index}:${config.currency}:${config.country}:${config.locale}`;

async function fetchPage(page) {
  const url = new URL(config.index, location.origin);
  url.searchParams.set('page', String(page));
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error('Catalog unavailable');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const element = html.getElementById('cc-catalog-data');
    if (!element) throw new Error('Catalog data missing');
    const data = JSON.parse(element.textContent);
    if (!Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages > 100) throw new Error('Invalid catalog');
    return data;
  } finally { clearTimeout(timer); }
}

async function loadCatalog() {
  try {
    const cached = JSON.parse(sessionStorage.getItem(cacheKey));
    if (cached && Date.now() - cached.time < 300000 && Array.isArray(cached.products)) return cached.products;
  } catch {}
  const first = await fetchPage(1);
  const pages = [first];
  let next = 2;
  await Promise.all(Array.from({ length: Math.min(3, Math.max(0, first.pages - 1)) }, async () => {
    while (next <= first.pages) {
      const page = next++;
      pages.push(await fetchPage(page));
    }
  }));
  const products = Array.from(new Map(pages.flatMap((page) => page.products).map((product) => [product.id, product])).values());
  try { sessionStorage.setItem(cacheKey, JSON.stringify({ time: Date.now(), products })); } catch {}
  return products;
}

function ensureCatalog() {
  if (!catalogPromise) catalogPromise = loadCatalog().then((products) => products.map((product) => {
    const primary = words(`${product.title} ${product.sku} ${product.barcode}`);
    const text = words(`${primary} ${product.vendor} ${product.type} ${product.description}`);
    return { ...product, primary, text: `${text} ${compact(primary)}` };
  })).catch((error) => { catalogPromise = undefined; throw error; });
  return catalogPromise;
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

class CCLiveSearch extends HTMLElement {
  connectedCallback() {
    if (this.connected) return;
    this.connected = true;
    this.input = this.querySelector('input[name="q"]');
    this.form = this.querySelector('form');
    this.panel = this.querySelector('[data-cc-results]');
    if (!this.input || !this.form || !this.panel) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.version = 0;
    this.limit = this.dataset.mode === 'page' ? 24 : 8;
    this.panel.id = `${this.input.id}-matches`;
    if (this.dataset.mode !== 'page') {
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-controls', `${this.panel.id}-list`);
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-expanded', 'false');
    }
    this.input.addEventListener('focus', () => {
      ensureCatalog().catch(() => {});
      if (this.input.value.trim()) this.search();
    }, { signal });
    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.version++;
      this.active = -1;
      this.limit = this.dataset.mode === 'page' ? 24 : 8;
      this.timer = setTimeout(() => this.search(), 120);
    }, { signal });
    this.input.addEventListener('keydown', (event) => this.keydown(event), { signal });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (this.dataset.mode === 'page') { this.search(); return; }
      const url = new URL(this.form.action, location.origin);
      url.searchParams.set('q', this.input.value.trim());
      url.searchParams.set('type', 'product');
      location.assign(url.href);
    }, { signal });
    this.form.addEventListener('reset', () => {
      this.version++;
      this.hide();
    }, { signal });
    document.addEventListener('click', (event) => {
      if (this.dataset.mode === 'compact' && !this.contains(event.target)) this.hide();
    }, { signal });
    if (this.dataset.mode === 'page') this.search();
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.timer);
    this.connected = false;
  }

  hide() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  status(text) {
    this.panel.hidden = false;
    this.panel.replaceChildren(element('p', 'cc-search-status', text));
    this.panel.firstChild.setAttribute('role', 'status');
    this.panel.firstChild.setAttribute('aria-live', 'polite');
    this.input.setAttribute('aria-expanded', 'false');
  }

  async search() {
    const query = words(this.input.value);
    const version = ++this.version;
    if (compact(query).length < 1) {
      if (query || this.dataset.mode === 'page') this.status(config.min);
      else this.hide();
      return;
    }
    this.status(config.loading);
    try {
      const products = await ensureCatalog();
      if (version !== this.version || !this.isConnected) return;
      const tokens = query.split(/\s+/);
      const matches = products.filter((product) => tokens.every((token) => product.text.includes(token)));
      const score = (product) => (compact(product.sku) === compact(query) ? 1000 : 0) + (words(product.title).startsWith(query) ? 300 : 0) + (product.primary.includes(query) ? 100 : 0);
      matches.sort((a, b) => score(b) - score(a) || a.title.localeCompare(b.title, config.locale));
      this.render(matches);
    } catch {
      if (version !== this.version) return;
      this.status(config.error);
      const retry = element('button', 'cc-search-more', config.retry);
      retry.type = 'button';
      retry.addEventListener('click', () => this.search(), { once: true });
      const fallback = element('a', 'cc-search-fallback', config.fallback);
      const url = new URL(config.search, location.origin);
      url.searchParams.set('q', this.input.value);
      url.searchParams.set('type', 'product');
      url.searchParams.set('view', 'cc-native');
      fallback.href = url.href;
      fallback.dataset.ccNative = 'true';
      this.panel.append(retry, fallback);
    }
  }

  render(matches) {
    this.panel.hidden = false;
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.panel.replaceChildren();
    const status = element('p', 'cc-search-status', matches.length ? config.count.replace('__COUNT__', matches.length) : config.empty);
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    this.panel.append(status);
    const list = element('ul', 'cc-search-list');
    list.id = `${this.panel.id}-list`;
    if (this.dataset.mode !== 'page') list.setAttribute('role', 'listbox');
    const money = new Intl.NumberFormat(config.currency === 'MXN' ? 'es-MX' : config.locale, { style: 'currency', currency: config.currency });
    for (const [index, product] of matches.slice(0, this.limit).entries()) {
      const item = element('li', 'cc-search-item');
      if (this.dataset.mode !== 'page') item.setAttribute('role', 'presentation');
      const link = element('a', 'cc-search-link');
      const url = new URL(product.url, location.origin);
      if (url.origin !== location.origin) continue;
      link.href = url.href;
      link.id = `${this.panel.id}-item-${index}`;
      if (this.dataset.mode !== 'page') { link.setAttribute('role', 'option'); link.setAttribute('aria-selected', 'false'); }
      if (product.image) {
        const image = element('img', 'cc-search-image');
        image.src = product.image;
        image.alt = '';
        image.width = 80;
        image.height = 80;
        image.loading = 'lazy';
        link.append(image);
      }
      const info = element('span', 'cc-search-info');
      const kind = window.ccProductKind?.(product.title);
      if (kind) info.append(element('span', 'cc-kind', kind));
      info.append(element('span', 'cc-search-title', product.title));
      if (product.sku) info.append(element('span', 'cc-search-sku', `SKU: ${product.sku}`));
      const prices = element('span', 'cc-search-prices');
      prices.append(element('strong', '', money.format(product.price / 100)));
      if (product.compare > product.price) prices.append(element('s', '', money.format(product.compare / 100)));
      info.append(prices);
      if (product.backorder) {
        const note = element('span', 'cc-search-stock');
        note.append(element('strong', '', config.stock), element('span', '', config.lead));
        info.append(note);
      } else if (!product.available) info.append(element('span', 'cc-search-soldout', config.soldout));
      link.append(info);
      item.append(link);
      list.append(item);
    }
    this.panel.append(list);
    this.input.setAttribute('aria-expanded', matches.length ? 'true' : 'false');
    if (matches.length > this.limit) {
      if (this.dataset.mode === 'page') {
        const button = element('button', 'cc-search-more', config.more);
        button.type = 'button';
        button.addEventListener('click', () => { this.limit += 24; this.render(matches); });
        this.panel.append(button);
      } else {
        const link = element('a', 'cc-search-more', config.all.replace('__COUNT__', matches.length));
        const url = new URL(config.search, location.origin);
        url.searchParams.set('q', this.input.value.trim());
        url.searchParams.set('type', 'product');
        link.href = url.href;
        this.panel.append(link);
      }
    }
  }

  keydown(event) {
    if (event.key === 'Escape') { this.hide(); return; }
    const links = [...this.panel.querySelectorAll('.cc-search-link')];
    if (!links.length || this.panel.hidden) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.active = ((this.active ?? -1) + direction + links.length) % links.length;
      links.forEach((link, index) => { link.setAttribute('aria-selected', String(index === this.active)); });
      this.input.setAttribute('aria-activedescendant', links[this.active].id);
      links[this.active].scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && this.active >= 0) {
      event.preventDefault();
      links[this.active].click();
    }
  }
}

if (!customElements.get('cc-live-search')) customElements.define('cc-live-search', CCLiveSearch);

